import Component from 'core/Component';
import { deepMerge } from 'toolbox/deepMerge';
import { Event } from 'services/EventEmitter';
import { appendUrl } from 'toolbox/queryString';
import { ajax } from 'toolbox/ajax';

/**
 * This is a description of the EinsteinTracking constructor function.
 * @class
 * @classdesc This component contains all the activities tracking related to Einstein API.
 * This component is only injected in the storefront when the Einstein API Recommendations Provider is enabled.
 * @extends Component
 */
export default class EinsteinTracking extends Component {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            // This is URL to call to register an activity
            activityURL: undefined,
        }, options));
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        Event.on('analytics.event', this.onEvent, this);
    }

    /**
     * Handler called when sending basic events
     * @param { String } eventName - of the event
     * @param { Object } data - Data that needs to be passed in the event
     */
    onEvent(eventName, data = {}) {
        if (typeof eventName === 'object') {
            // if first argument is an object then we don't pass the eventName and use the default
            // The first param become the data object
            data = eventName;
        }

        switch (data.eventType) {
        case 'addProduct':
            if (!data.ecommerce || !data.ecommerce.add) {
                break;
            }

            this.triggerActivity({
                activityName: 'addToCart',
                data: {
                    products: data.ecommerce.add.products.map(product => ({
                        ID: product.id,
                        quantity: product.quantity,
                        price: product.price,
                    })),
                },
            });
            break;
        case 'productImpression':
            data.product.forEach((product) => {
                const { recommenderName } = product;

                if (recommenderName) {
                    this.triggerActivity({
                        activityName: 'viewRecommendation',
                        data: {
                            recommenderName,
                            products: [{ ID: product.id }],
                        },
                    });
                }
            });
            break;
        case 'productDetail':
            data.ecommerce.detail.products.forEach((product) => {
                this.triggerActivity({
                    activityName: 'viewProduct',
                    data: {
                        product: {
                            ID: product.id,
                        },
                    },
                });
            });
            break;
        case 'productClick':
            // Only register clicks on recommendations tiles, meaning that the recommenderName is provided on the product object
            // Skip the others
            data.ecommerce.click.products
                .filter(product => typeof product.recommenderName !== 'undefined' && product.recommenderName !== '')
                .forEach((product) => {
                    this.triggerActivity({
                        activityName: 'clickRecommendation',
                        data: {
                            recommenderName: product.recommenderName,
                            product: {
                                ID: product.pid,
                            },
                        },
                    });
                });
            break;
        default:
            // Other event types are not supported by the Einstein API
            break;
        }
    }

    /**
     * Trigger the activity event if the activity URL is provided as option
     *
     * @param {Object} eventData The data to pass to the event
     */
    triggerActivity(eventData) {
        const activityURL = this.options.activityURL || undefined;
        if (!activityURL) {
            return;
        }

        eventData.data = encodeURIComponent(JSON.stringify(eventData.data));
        const url = appendUrl(activityURL, eventData);

        if (navigator.sendBeacon) {
            navigator.sendBeacon(url);
        } else {
            ajax(url, { method: 'POST' });
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        Event.removeListener('analytics.event', this.onEvent, this);
    }
}
